
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ServiceCard from './components/ServiceCard';
import CTASection from '../../components/feature/CTASection';

export default function ServicesPage() {
  useEffect(() => {
    document.title = 'AI Revenue Systems & Growth Services | Sacramento CA | SparkBoost Labs';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Transform your service business with predictable revenue systems. AI-powered outbound automation, sales optimization, and growth consulting for Sacramento companies. Free assessment available.');
    }
  }, []);

  const coreServices = [
    {
      icon: 'ri-robot-2-line',
      title: 'AI-Powered Outbound Automation',
      description: 'Transform cold outreach into warm conversations with intelligent automation systems that book qualified meetings while you sleep.',
      features: [
        'Smart lead qualification & scoring',
        'Personalized outreach sequences',
        'Multi-channel campaign orchestration',
        'Real-time performance optimization'
      ],
      results: '40% increase in qualified meetings',
      caseStudy: 'TechConsult Pro generated 127 new leads in 90 days'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Sales Process Optimization',
      description: 'Eliminate revenue bottlenecks with data-driven sales processes that convert prospects into loyal customers consistently.',
      features: [
        'Sales funnel analysis & optimization',
        'CRM integration & automation',
        'Performance tracking dashboards',
        'Team training & enablement'
      ],
      results: '65% improvement in close rates',
      caseStudy: 'Legal Solutions Inc increased revenue by 300% in 6 months'
    },
    {
      icon: 'ri-megaphone-line',
      title: 'Authority-Building Content Systems',
      description: 'Position your expertise as the go-to solution with content that attracts, educates, and converts your ideal clients.',
      features: [
        'Thought leadership content strategy',
        'SEO-optimized blog & resources',
        'Social media content automation',
        'Email nurture sequences'
      ],
      results: '180% increase in organic traffic',
      caseStudy: 'Growth Partners saw 5x increase in inbound leads'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Revenue Operations Consulting',
      description: 'Align your sales, marketing, and operations teams with systems that scale revenue predictably and sustainably.',
      features: [
        'Revenue strategy development',
        'Process documentation & SOPs',
        'Technology stack optimization',
        'Team alignment & training'
      ],
      results: '250% average revenue growth',
      caseStudy: 'Consulting Dynamics scaled from $500K to $2M ARR'
    }
  ];

  const aiSolutions = [
    {
      icon: 'ri-robot-line',
      title: 'AI-Powered CRM Assistant',
      description: 'An intelligent CRM assistant that automates data entry, predicts customer behavior, and personalizes communication to enhance sales and customer service efficiency.',
      category: 'Customer Relationship Management',
      benefits: 'Streamlines customer relationship management, reduces manual workload, improves lead conversion rates, and fosters stronger customer loyalty through tailored interactions.'
    },
    {
      icon: 'ri-search-line',
      title: 'Lead Generation Solutions',
      description: 'AI platforms designed to identify, qualify, and nurture potential leads for your business. It leverages machine learning algorithms to analyze market trends, customer behavior, and demographic data to pinpoint high-value prospects.',
      category: 'Sales & Marketing',
      benefits: 'Boosts sales pipeline efficiency, improves lead quality and reduces customer acquisition costs.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Predictive Analytics Engine',
      description: 'Analyzes vast datasets to forecast market trends, customer behavior, and potential operational risks with high accuracy.',
      category: 'Business Intelligence',
      benefits: 'Enables proactive decision-making, optimizes resource allocation, and identifies new growth opportunities.'
    },
    {
      icon: 'ri-article-line',
      title: 'Automated Content Generator',
      description: 'Creates high-quality articles, marketing copy, and social media posts in various styles and tones, tailored to your brand.',
      category: 'Content Generation',
      benefits: 'Speeds up content creation, ensures brand consistency across platforms, and significantly reduces copywriting costs.'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Robotic Process Automation (RPA) Bot',
      description: 'Automates repetitive, rule-based tasks across different software applications, such as data entry, invoice processing, and report generation.',
      category: 'Automation',
      benefits: 'Boosts operational efficiency, minimizes human error, and frees up staff for more strategic and creative work.'
    },
    {
      icon: 'ri-truck-line',
      title: 'Intelligent Supply Chain Optimizer',
      description: 'Uses AI to optimize logistics, inventory management, and demand forecasting for complex global supply chains.',
      category: 'Operations',
      benefits: 'Reduces operational costs, improves delivery times, enhances supply chain resilience, and minimizes waste.'
    },
    {
      icon: 'ri-team-line',
      title: 'Virtual HR Assistant',
      description: 'Handles routine HR queries, streamlines onboarding processes, and provides 24/7 employee support through an intelligent chatbot.',
      category: 'Human Resources',
      benefits: 'Streamlines HR operations, improves employee experience, and significantly reduces administrative burden on HR staff.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Cybersecurity Threat Detector',
      description: 'Employs advanced machine learning algorithms to identify and neutralize sophisticated cyber threats and anomalies in real-time.',
      category: 'Security',
      benefits: 'Enhances data security, prevents costly breaches, ensures compliance with industry regulations, and protects sensitive assets.'
    },
    {
      icon: 'ri-code-line',
      title: 'Code Generation Assistant',
      description: 'Assists developers by generating code snippets, suggesting optimizations, and identifying potential bugs across multiple programming languages.',
      category: 'Software Development',
      benefits: 'Accelerates development cycles, improves code quality, reduces debugging time, and fosters innovation.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1B365D] to-[#2BB6A3] py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl font-bold mb-6">
                AI-Powered Revenue Systems for Service-Based Companies
              </h1>
              <p className="text-xl mb-8 opacity-90">
                Transform your business with predictable revenue systems that scale beyond founder-led sales. 
                Our proven methodology has helped 75+ companies achieve 300% average revenue growth.
              </p>
              <div className="flex flex-wrap justify-center gap-8 mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold">75+</div>
                  <div className="text-sm opacity-80">Companies Transformed</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">300%</div>
                  <div className="text-sm opacity-80">Average Revenue Increase</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">90</div>
                  <div className="text-sm opacity-80">Days to First Results</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Services Grid */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#1B365D] mb-4">
                Our Core Services
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We specialize in building predictable revenue systems that eliminate the feast-or-famine cycle 
                and create sustainable growth for service-based businesses.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {coreServices.map((service, index) => (
                <ServiceCard key={index} service={service} />
              ))}
            </div>
          </div>
        </section>

        {/* AI-Powered Business Solutions */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#1B365D] mb-4">
                AI-Powered Business Solutions
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Transform your business operations with cutting-edge AI solutions designed to automate processes, 
                enhance decision-making, and drive sustainable growth across all departments.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {aiSolutions.map((solution, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-[#2BB6A3]/10 rounded-2xl mb-6 group-hover:bg-[#2BB6A3]/20 transition-all duration-300">
                    <i className={`${solution.icon} text-3xl text-[#2BB6A3]`}></i>
                  </div>
                  
                  <div className="mb-3">
                    <span className="inline-block bg-[#1B365D]/10 text-[#1B365D] text-xs font-semibold px-3 py-1 rounded-full">
                      {solution.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-[#1B365D] mb-4">
                    {solution.title}
                  </h3>
                  
                  <p className="text-[#2A2D34] mb-4 leading-relaxed text-sm">
                    {solution.description}
                  </p>

                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-sm font-semibold text-[#1B365D] mb-2">Key Benefits:</h4>
                    <p className="text-[#2A2D34] text-sm leading-relaxed">
                      {solution.benefits}
                    </p>
                  </div>

                  <div className="mt-6">
                    <a 
                      href="/free-assessment"
                      className="w-full bg-[#1B365D] text-white px-4 py-3 rounded-lg font-semibold hover:bg-[#2BB6A3] transition-all duration-200 cursor-pointer whitespace-nowrap text-sm inline-block text-center"
                    >
                      Learn More
                      <i className="ri-arrow-right-line ml-2"></i>
                    </a>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <a 
                href="/free-assessment"
                className="bg-[#2BB6A3] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#1B365D] transition-all duration-200 cursor-pointer whitespace-nowrap inline-block"
              >
                Get Your Free Assessment
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </section>

        {/* Process Overview */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#1B365D] mb-4">
                Our Proven Process
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                A systematic approach to building predictable revenue systems that scale with your business.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#2BB6A3] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1B365D] mb-2">1. Discovery & Audit</h3>
                <p className="text-gray-600">Analyze current systems and identify revenue opportunities</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-[#2BB6A3] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-lightbulb-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1B365D] mb-2">2. Strategy Design</h3>
                <p className="text-gray-600">Create customized revenue system blueprint for your business</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-[#2BB6A3] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-tools-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1B365D] mb-2">3. Implementation</h3>
                <p className="text-gray-600">Build and deploy AI-powered automation systems</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-[#2BB6A3] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-line-chart-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1B365D] mb-2">4. Optimization</h3>
                <p className="text-gray-600">Monitor, measure, and continuously improve performance</p>
              </div>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
}
